// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Info, Lock } from "lucide-react";
import { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { buildUserEditBaseBody, useUpdateUserMutation, useUserByPublicIdQuery } from "@/api/hooks/use-users";
import { PageFooter } from "@/components/layout/page-footer";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  FULL_ACCESS,
  KNOWN_PRODUCT_PERMISSIONS as KNOWN_PERMISSIONS,
  productPermissionMatches as matches,
  normalizeProduct as normalize,
  PERMISSION_DESCRIPTIONS,
  productPermissionSetsEqual as permissionSetsEqual,
  REGULAR_SECTIONS,
} from "@/features/iam/permission-trees";
import { countSetChanges } from "@/lib/dirty-fields";
import { cn } from "@/lib/utils";

export interface UserPermissionsTabHandle {
  reset: () => void;
}

interface UserPermissionsTabProps {
  publicId: string;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<UserPermissionsTabHandle>;
}

export function UserPermissionsTab({ publicId, onDirtyChange, ref }: UserPermissionsTabProps) {
  const { t } = useTranslation("back-office");
  const { data } = useUserByPublicIdQuery(publicId);
  const updateUser = useUpdateUserMutation(publicId);

  // All permissions assigned directly to the user (across every domain) — used on save to preserve
  // the slices this tab doesn't manage (backoffice-admin permissions, unknown grants).
  const allDirectPermissions = useMemo(
    () =>
      (data?.permissions ?? []).filter((source) => source.groupName === null).flatMap((source) => source.permissions),
    [data],
  );

  // Permissions assigned directly to the user, scoped to the product-permission domain (the editable set).
  const directPermissions = useMemo(() => {
    const set = new Set<string>();
    for (const source of data?.permissions ?? []) {
      if (source.groupName !== null) continue;
      for (const permission of source.permissions) {
        const normalized = normalize(permission);
        if (KNOWN_PERMISSIONS.has(normalized)) set.add(normalized);
      }
    }
    return set;
  }, [data]);

  // Permissions inherited from groups (read-only here) → group they come from.
  const inheritedList = useMemo(() => {
    const list: { permission: string; groupName: string }[] = [];
    for (const source of data?.permissions ?? []) {
      if (source.groupName === null) continue;
      for (const permission of source.permissions) {
        const normalized = normalize(permission);
        if (KNOWN_PERMISSIONS.has(normalized)) list.push({ permission: normalized, groupName: source.groupName });
      }
    }
    return list;
  }, [data]);

  const inheritedGroups = useMemo(
    () => [...new Set(inheritedList.map((entry) => entry.groupName))].sort((a, b) => a.localeCompare(b)),
    [inheritedList],
  );

  // Baseline the dirty check compares against. It tracks the persisted state, but we advance it on a
  // successful save so the form clears immediately — without waiting for the refetched data.
  const [baseline, setBaseline] = useState<Set<string>>(() => new Set(directPermissions));
  const [enabled, setEnabled] = useState<Set<string>>(() => new Set(directPermissions));

  useEffect(
    function resetWhenDataLoads() {
      setEnabled(new Set(directPermissions));
      setBaseline(new Set(directPermissions));
    },
    [directPermissions],
  );

  const isDirty = !permissionSetsEqual(enabled, baseline);

  useImperativeHandle(ref, () => ({ reset: () => setEnabled(new Set(baseline)) }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  // The group (if any) whose grant satisfies `required` — exact or via wildcard.
  function inheritedFrom(required: string): string | undefined {
    return inheritedList.find((entry) => matches(entry.permission, required))?.groupName;
  }
  // A broader directly-granted permission that already covers `required`.
  function coveredByOther(required: string): string | undefined {
    for (const granted of enabled) {
      if (granted !== required && matches(granted, required)) return granted;
    }
    return undefined;
  }

  function toggle(permission: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return next;
    });
  }

  function renderSwitch(permission: string) {
    const inheritedGroup = inheritedFrom(permission);
    if (inheritedGroup) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Switch checked disabled aria-readonly className="pointer-events-none opacity-60" />
              <Lock className="h-3 w-3" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {t("users.permissions.inheritedFromGroup", { group: inheritedGroup })}
          </TooltipContent>
        </Tooltip>
      );
    }

    const covered = coveredByOther(permission);
    if (covered) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center text-muted-foreground">
              <Switch checked disabled aria-readonly className="pointer-events-none opacity-60" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="font-mono text-xs">
            {t("users.permissions.grantedVia", { permission: covered })}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <Switch checked={enabled.has(permission)} onCheckedChange={() => toggle(permission)} />;
  }

  async function onSave() {
    if (!data) return;
    // Resend the full direct-permission set: the slices this tab doesn't manage (preserved as-is)
    // plus the switches enabled here. Legacy full wildcards are normalised to their canonical form.
    const preserved = allDirectPermissions.filter((permission) => !KNOWN_PERMISSIONS.has(normalize(permission)));
    const permissions = [...new Set([...preserved, ...enabled])];
    try {
      await updateUser.mutateAsync({ ...buildUserEditBaseBody(data), permissions });
      // Clear the dirty state right away, ahead of the refetch reconciling `directPermissions`.
      setBaseline(new Set(enabled));
      toast.success(t("users.notifications.updated", { name: data.user.fullName || data.user.email }));
    } catch {
      // Error toast handled by API middleware
    }
  }

  function gridRow(
    key: string,
    label: React.ReactNode,
    permission: string,
    variant?: "section" | "node",
    separator?: boolean,
    description?: string,
  ) {
    const labelClass = variant === "section" ? "font-medium" : variant === "node" ? "font-mono text-xs" : undefined;
    // The label column stays open; a section divider is drawn as `border-b` on the row *above* a
    // section (`separator`) so it lines up exactly with the switch column's bottom borders.
    return (
      <Fragment key={key}>
        <div className={cn("px-3 py-2", variant === "node" && "pl-6", separator && "border-b")}>
          {description ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-4",
                    labelClass,
                  )}
                >
                  {label}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">{description}</TooltipContent>
            </Tooltip>
          ) : (
            <span className={labelClass}>{label}</span>
          )}
        </div>
        <div className="min-h-9 justify-center py-1.5">{renderSwitch(permission)}</div>
      </Fragment>
    );
  }

  return (
    <div className="max-w-2xl space-y-6 p-6">
      {inheritedGroups.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t("users.permissions.inheritBanner.prefix")}{" "}
            {inheritedGroups.map((group, index) => (
              <span key={group}>
                {index > 0 && ", "}
                <span className="font-medium text-foreground">{group}</span>
              </span>
            ))}
            . {t("users.permissions.inheritBanner.suffix")}
          </span>
        </div>
      )}

      <div
        className={cn(
          "grid grid-cols-[1fr_3.5rem] text-sm",
          "[&>*]:flex [&>*]:items-center",
          // Borders only around the single switch column — the label column stays open.
          "[&>*:nth-child(2n)]:border-x [&>*:nth-child(2n)]:border-b",
          "[&>*:nth-child(2)]:border-t",
        )}
      >
        {gridRow(
          "__full-access",
          t("users.permissions.fullAccess"),
          FULL_ACCESS,
          "section",
          true,
          t("users.permissions.everyPermission"),
        )}

        {REGULAR_SECTIONS.map((section, sectionIndex) => {
          const isLastSection = sectionIndex === REGULAR_SECTIONS.length - 1;
          return (
            <Fragment key={section.prefix}>
              {gridRow(
                `${section.prefix}__header`,
                t(section.label),
                `${section.prefix}:*`,
                "section",
                // A wildcard-only section has no rows to carry the divider, so its header does.
                !isLastSection && section.permissions.length === 0,
                t("users.permissions.allPermissions", { section: t(section.label) }),
              )}
              {section.permissions.map((permission, permissionIndex) =>
                gridRow(
                  permission,
                  permission,
                  permission,
                  "node",
                  // Divider below the last row of every section except the last — lines up the open
                  // label column with the next section's top border.
                  !isLastSection && permissionIndex === section.permissions.length - 1,
                  PERMISSION_DESCRIPTIONS[permission],
                ),
              )}
            </Fragment>
          );
        })}
      </div>

      {isDirty && (
        <PageFooter>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSave();
            }}
          >
            <SaveDiscardPane
              docked
              onDiscard={() => setEnabled(new Set(baseline))}
              saving={updateUser.isPending}
              changeCount={countSetChanges(enabled, baseline)}
            />
          </form>
        </PageFooter>
      )}
    </div>
  );
}
